import {StagedUploadTargetGenerateUploadResource} from 'types/graphql/default-types';
import type {MediaValidationResult} from 'utilities/validate-image';
import {ImageDefaults, MediaValidationStatus} from 'utilities/validate-image';

import type {MediasUploadResults, StagedUploadResult} from '../types';

interface MediaValidation {
  error: string;
  replacements: {
    fileName: string;
    maxMegapixels?: number;
    maximumMegabytes?: number;
    validImageTypes?: string;
  };
}

export const validateMediaResults = (
  {status}: MediaValidationResult,
  name: string,
): MediaValidation | undefined => {
  switch (status) {
    case MediaValidationStatus.CorruptedFile:
      return {
        error: 'Common.validation.fileUnsupportedOrCorruptedError',
        replacements: {
          fileName: name,
          validImageTypes: ImageDefaults.validExtensions.join(', '),
        },
      };
    case MediaValidationStatus.ExceedsMaximumDimensions:
      return {
        error: 'Common.validation.fileExceedsMaxMegapixels',
        replacements: {
          fileName: name,
          maxMegapixels: ImageDefaults.maxMegapixels,
        },
      };
    case MediaValidationStatus.ExceedsMaximumSize:
      return {
        error: 'Common.validation.fileSizeErrorMb',
        replacements: {
          fileName: name,
          maximumMegabytes: ImageDefaults.maxFileSizeInMegabytes,
        },
      };
    case MediaValidationStatus.InvalidImageFileType:
      return {
        error: 'Common.validation.imageTypeError',
        replacements: {
          fileName: name,
          validImageTypes: ImageDefaults.validExtensions.join(', '),
        },
      };
  }
};

const getImageKeyFromAWSResponse = (response: Response) => {
  return response
    .text()
    .then((xmlString) => new DOMParser().parseFromString(xmlString, 'text/xml'))
    .then((data) => data.getElementsByTagName('Key')[0].textContent);
};

export const getPath = async (
  response: Response,
  stagedUploadResult: StagedUploadResult,
  resourceType?: StagedUploadTargetGenerateUploadResource,
) => {
  if (resourceType) {
    const imageKey = await getImageKeyFromAWSResponse(response);
    const newURL = new URL(stagedUploadResult.url);
    newURL.pathname = imageKey || '';

    return newURL.href;
  }

  return stagedUploadResult.resourceUrl!;
};

export const getStagedUploadTargetGenerateUploadResourceType = ({
  type,
}: File) => {
  if (type.startsWith('image/')) {
    return StagedUploadTargetGenerateUploadResource.Image;
  }

  if (type.startsWith('video/')) {
    return StagedUploadTargetGenerateUploadResource.Video;
  }

  return StagedUploadTargetGenerateUploadResource.Model_3D;
};

export const getMediaUserErrorMessage = (
  mediasUploadResults: MediasUploadResults,
  index: number,
): string | undefined => {
  const userErrorFound = mediasUploadResults.userErrors.find((userError) => {
    if (userError.field) {
      const isMediaLevelUserError = userError.field.length === 2;

      if (isMediaLevelUserError) {
        return userError.field[1] === index.toString();
      }
    }

    return false;
  });

  return userErrorFound?.message;
};
